import { Head, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { serviceEditorPath, serviceEditorSectionPath, servicePath } from "@travily/routes";
import type ListingsEditorPhotos from "@travily/types/pages/ListingsEditorPhotos";
import { ButtonDock } from "@ui/components/ButtonDock";
import { StandardButton } from "@ui/components/StandardButton";
import type { ReactNode } from "react";
import { UnsavedChangesDialog } from "@/components/UnsavedChangesDialog/UnsavedChangesDialog";
import { useStagedPhotoManager } from "@/hooks/listings/use-staged-photo-manager";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { buildEditorSections } from "@/lib/editor-sections";
import { ServicePhotosEditor } from "./show.view";

const ServiceEditPhotosPage = (props: ListingsEditorPhotos) => {
  const { listing_id, photos, min_photos } = props;
  const { errors } = usePage().props;
  const sections = buildEditorSections(props);
  const manager = useStagedPhotoManager({
    photos,
    saveUrl: serviceEditorSectionPath(listing_id, "photos"),
  });
  const guard = useUnsavedChangesGuard(manager.isDirty);
  const isSaveDisabled = !manager.isDirty || manager.isSaving || manager.upload.isUploading;

  return (
    <EditFrame
      activeSection="photos"
      actions={
        <ButtonDock
          primaryAction={
            <>
              <StandardButton
                className="sm:hidden"
                color="green"
                isDisabled={isSaveDisabled}
                onPress={manager.save}
                size="56"
              >
                {t("common.actions.save")}
              </StandardButton>
              <StandardButton
                className="hidden sm:flex"
                color="green"
                isDisabled={isSaveDisabled}
                onPress={manager.save}
                size="64"
              >
                {t("common.actions.save")}
              </StandardButton>
            </>
          }
        />
      }
      backHref={servicePath(listing_id)}
      href={serviceEditorPath(listing_id)}
      sections={sections}
      title={t("web.services.editors.show.title")}
    >
      <Head title={t("web.services.editor.sections.show.meta_title", { title: props.listing_title ?? "" })} />
      <ServicePhotosEditor errors={errors} manager={manager} minPhotos={min_photos} />
      <UnsavedChangesDialog
        isOpen={guard.isDialogOpen}
        onConfirm={guard.confirmLeave}
        onOpenChange={(isOpen) => {
          if (!isOpen) guard.stay();
        }}
      />
    </EditFrame>
  );
};

ServiceEditPhotosPage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default ServiceEditPhotosPage;
